import { ApiError } from '@/lib/errors';

const delay = (ms: number): Promise<void> =>
  new Promise<void>(r => setTimeout(r, ms));

const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_BASE_DELAY_MS = 300;

interface RetryOptions {
  maxRetries?: number;
  baseDelayMs?: number;
}

/**
 * Retries `fn` on HTTP 503 only (transient service unavailable / query timeout).
 * Deliberately does NOT retry other 5xx codes: a 500 is a server bug, a 502/504
 * is a gateway error — both indicate a persistent problem, not a transient wobble.
 * For UI-side "is this a transient error?" checks, use `isTransientError` from
 * `@/lib/errors`, which classifies all 5xx as transient for display purposes.
 */
export async function retryOn503<T>(
  fn: () => Promise<T>,
  opts?: RetryOptions
): Promise<T> {
  const maxRetries = opts?.maxRetries ?? DEFAULT_MAX_RETRIES;
  const baseDelayMs = opts?.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;

  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (e) {
      if (
        e instanceof ApiError &&
        e.statusCode === 503 &&
        attempt < maxRetries
      ) {
        await delay(baseDelayMs * 2 ** attempt + Math.random() * baseDelayMs);
        continue;
      }
      throw e;
    }
  }
}
